import { Lock, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { TextareaField } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import { HireDialog } from '@/pages/admin/applicants/HireDialog';
import {
  APPLICANT_TONES,
  useApplicant,
  useSetApplicantNotes,
  useSetApplicantStatus,
} from '@/hooks/applicants';
import { ApiError } from '@/lib/api';
import { formatDay, formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { ApplicantDetail } from '@shared/schemas/applicant';

/*
 * /admin/applicants/:id — the whole application, ROADMAP.md §9.10.
 *
 * **This page is the reason the slice exists.** An application spans five
 * tables: the employment history, the references, the days available and the
 * types of cleaning experience are all child rows, and a detail view that
 * rendered the top-level row and stopped would look complete while dropping four
 * of the things somebody hires on. That is the discard slice 8.7 was written to
 * prevent, one size smaller — so every block below is here because the live form
 * collects it, checked against reference/prototype-copy/careers-form.md rather
 * than against a summary.
 *
 * **It is also the most sensitive screen in the admin portal.** A home address,
 * a phone number, an employment history and two named references belonging to a
 * real person who applied for a job. Admin-only, server-side, and nothing here
 * is logged or exported.
 */

export function ApplicantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const applicant = useApplicant(id);
  const [hiring, setHiring] = useState(false);

  if (applicant.isError) {
    return (
      <AdminPage title="Applicant">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="That application could not be loaded"
            onRetry={() => {
              void applicant.refetch();
            }}
          />
        </div>
      </AdminPage>
    );
  }

  if (applicant.isPending) {
    return (
      <AdminPage title="Applicant">
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading the application</span>
          <div aria-hidden="true" className="h-32 animate-pulse rounded-lg bg-white" />
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-white" />
        </div>
      </AdminPage>
    );
  }

  const detail = applicant.data;

  return (
    <AdminPage
      title={detail.fullName}
      record={detail.fullName}
      description={
        detail.positionName === null
          ? 'The position they applied for has since been removed.'
          : `Applied for ${detail.positionName} · ${formatInstant(detail.submittedAt)}`
      }
      actions={
        <Actions
          detail={detail}
          onHire={() => {
            setHiring(true);
          }}
        />
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge label={detail.statusLabel} tone={APPLICANT_TONES[detail.status]} />
        {detail.reviewedAt ? (
          <span className="text-meta text-ink-500">
            First reviewed {formatInstant(detail.reviewedAt)}
            {detail.reviewedByName ? ` by ${detail.reviewedByName}` : ''}
          </span>
        ) : (
          <span className="text-meta text-ink-500">Nobody has reviewed this yet</span>
        )}
      </div>

      <p className="mb-6 flex items-start gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
        <Lock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        This is a real person&rsquo;s home address, phone number and work history. It is visible to
        admins only — cleaners and clients cannot reach it — and it is left out of exports.
      </p>

      <div className="space-y-4">
        <Section title="How to reach them">
          <dl className="grid gap-x-6 gap-y-2 text-body text-ink-700 sm:grid-cols-2">
            <Fact label="Email">
              <a
                href={`mailto:${detail.email}`}
                className="break-all text-bark-700 underline underline-offset-2"
              >
                {detail.email}
              </a>
            </Fact>
            <Fact label="Phone">
              <a
                href={`tel:${detail.phone}`}
                className="text-bark-700 underline underline-offset-2"
              >
                {detail.phone}
              </a>
            </Fact>
            <Fact label="Address">
              {/* Optional on the live form, so genuinely often blank. "Not given"
                  rather than a dash — an empty answer is still an answer (P1). */}
              {detail.address ?? <Absent>Not given</Absent>}
            </Fact>
            <Fact label="Town">
              {/* "Cedar City, UT 84720" — the state and the zip go together, the
                  way an address is written on an envelope. */}
              {[detail.city, [detail.state, detail.postalCode].filter(Boolean).join(' ')]
                .filter((part) => part !== null && part !== '')
                .join(', ') || <Absent>Not given</Absent>}
            </Fact>
          </dl>
        </Section>

        <Section title="The job they want">
          <dl className="grid gap-x-6 gap-y-2 text-body text-ink-700 sm:grid-cols-2">
            <Fact label="Position">
              {detail.positionName ?? <Absent>That position has been removed</Absent>}
            </Fact>
            <Fact label="Full-time or part-time">{detail.employmentTypeLabel}</Fact>
            <Fact label="Desired pay">
              {/* Free text on the form — "$18/hr", "negotiable". Never parsed. */}
              {detail.desiredPay ?? <Absent>Not given</Absent>}
            </Fact>
            <Fact label="Can start">
              {detail.availableStartDate ? (
                formatDay(detail.availableStartDate)
              ) : (
                <Absent>Not given</Absent>
              )}
            </Fact>
          </dl>

          {/*
            **Days Available — a hiring criterion, and it had no column until
            2026-08-04.** It decides whether somebody can cover a Saturday, and
            as originally specced slice 8.7 would have discarded it. In week
            order, not key order.
          */}
          <Chips
            label="Days available"
            values={detail.daysAvailable.map((entry) => entry.label)}
            empty="No days chosen"
          />
        </Section>

        <Section title="Cleaning experience">
          <dl className="grid gap-x-6 gap-y-2 text-body text-ink-700 sm:grid-cols-2">
            <Fact label="How long">
              {detail.hasCleaningExperience ? (
                (detail.experienceYearsLabel ?? <Absent>Not given</Absent>)
              ) : (
                // They ticked "I don't have Cleaning Experience". A real answer,
                // and not the same as leaving it blank.
                <span className="text-ink-900">None — new to cleaning</span>
              )}
            </Fact>
            <Fact label="Own supplies">{detail.hasOwnSupplies ? 'Yes' : 'No'}</Fact>
            <Fact label="Reliable transport">
              {detail.hasReliableTransportation ? 'Yes' : 'Not stated'}
            </Fact>
          </dl>

          {/*
            **Types of Cleaning Experience** — what makes an applicant a
            Commercial Cleaner rather than a House Cleaner. The other field that
            had no column until 2026-08-04.
          */}
          <Chips
            label="Types of cleaning"
            values={detail.experienceTypes.map((entry) => entry.label)}
            empty="None chosen"
          />
        </Section>

        <Section title="Work history">
          {!detail.hasWorkExperience ? (
            <p className="text-body text-ink-700">
              They said they have no work experience.
              <span className="ml-2 text-meta text-ink-500">
                The form asks, and answering honestly is allowed — the live form marks the employer
                block required beside that checkbox, so an honest career changer cannot submit it.
              </span>
            </p>
          ) : detail.employers.length === 0 ? (
            <Absent>No employers listed</Absent>
          ) : (
            <ol className="space-y-4">
              {detail.employers.map((employer, index) => (
                <li key={employer.id} className="border-l-2 border-honey-400 pl-3">
                  <p className="text-meta text-ink-500">
                    {index === 0 ? 'Most recent employer' : 'Previous employer'}
                  </p>
                  <p className="text-h3 text-ink-900">{employer.employerName}</p>
                  <p className="text-body text-ink-700">
                    {employer.jobTitle}
                    {employer.datesWorked ? (
                      // Free text on the form — "Jan 2022 – Mar 2024", "two
                      // summers". Never split into two dates.
                      <span className="text-ink-500"> · {employer.datesWorked}</span>
                    ) : null}
                  </p>
                  {employer.responsibilities ? (
                    <p className="mt-1.5 whitespace-pre-line text-body text-ink-700">
                      {employer.responsibilities}
                    </p>
                  ) : null}
                  {employer.reasonForLeaving ? (
                    <p className="mt-1.5 text-body text-ink-700">
                      <span className="text-meta text-ink-500">Why they left: </span>
                      {employer.reasonForLeaving}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section title="References">
          {!detail.hasReferences ? (
            <p className="text-body text-ink-700">They said they have no references to give.</p>
          ) : detail.references.length === 0 ? (
            <Absent>No references listed</Absent>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {detail.references.map((reference) => (
                <li key={reference.id} className="rounded-sm border border-ink-300 p-3">
                  <p className="text-body font-medium text-ink-900">{reference.name}</p>
                  <p className="text-body text-ink-700">
                    <a
                      href={`tel:${reference.phone}`}
                      className="text-bark-700 underline underline-offset-2"
                    >
                      {reference.phone}
                    </a>
                  </p>
                  <p className="text-meta text-ink-500">
                    {reference.relationship ?? 'Relationship not given'}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {detail.additionalInfo ? (
          <Section title="Anything else they wanted us to know">
            <p className="whitespace-pre-line text-body text-ink-700">{detail.additionalInfo}</p>
          </Section>
        ) : null}

        {/*
          **Keyed on the saved note**, so a save — or a refetch that picks up a
          note somebody wrote in another tab — remounts the editor with the
          stored value. Without it, this box would keep whatever was typed here
          and silently overwrite theirs on the next save.
        */}
        <InternalNotes key={detail.internalNotes ?? ''} detail={detail} />
      </div>

      <HireDialog detail={detail} open={hiring} onOpenChange={setHiring} />
    </AdminPage>
  );
}

/**
 * The per-state actions, straight from the prototype.
 *
 * `new` → Review · `reviewing` → Interview / Hire / Not hiring · `hired` → View
 * Employee. Two additions to it, both stated in
 * `@shared/schemas/applicant`'s transition table: an interview can go back to
 * reviewing, and a mis-clicked "Not hiring" can be reconsidered. There is a real
 * person at the other end of the row and one wrong click should not be the end
 * of it.
 */
function Actions({ detail, onHire }: { detail: ApplicantDetail; onHire: () => void }) {
  const toast = useToast();
  const setStatus = useSetApplicantStatus(detail.id);

  function move(status: 'reviewing' | 'interviewing' | 'not_hired', done: string) {
    setStatus.mutate(
      { status },
      {
        onSuccess: () => {
          toast.success(done);
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
        },
      },
    );
  }

  if (detail.status === 'hired') {
    return (
      <Button asChild>
        <Link to={`/admin/employees?search=${encodeURIComponent(detail.fullName)}`}>
          <UserCheck aria-hidden="true" />
          View employee
        </Link>
      </Button>
    );
  }

  const busy = setStatus.isPending;

  return (
    <>
      {detail.status === 'new' ? (
        <Button
          disabled={busy}
          onClick={() => {
            move('reviewing', `${detail.fullName} is now being reviewed.`);
          }}
        >
          Review
        </Button>
      ) : null}

      {detail.status === 'reviewing' ? (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            move('interviewing', `${detail.fullName} is down for an interview.`);
          }}
        >
          Interview
        </Button>
      ) : null}

      {detail.status === 'reviewing' || detail.status === 'interviewing' ? (
        <Button disabled={busy} onClick={onHire}>
          Hire
        </Button>
      ) : null}

      {detail.status === 'not_hired' ? (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            move('reviewing', `${detail.fullName} is back in the pipeline.`);
          }}
        >
          Reconsider
        </Button>
      ) : (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            move('not_hired', `Marked ${detail.fullName} as not hired.`);
          }}
        >
          Not hiring
        </Button>
      )}
    </>
  );
}

/**
 * The office's own remarks.
 *
 * Distinct from anything the applicant wrote and never sent anywhere. Saved on a
 * button rather than on blur, because a note somebody is halfway through typing
 * is not a note they have finished writing.
 */
function InternalNotes({ detail }: { detail: ApplicantDetail }) {
  const toast = useToast();
  const [notes, setNotes] = useState(detail.internalNotes ?? '');
  const save = useSetApplicantNotes(detail.id);

  const dirty = notes !== (detail.internalNotes ?? '');

  return (
    <Section title="Internal notes">
      <TextareaField
        label="Notes"
        name="internalNotes"
        rows={4}
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
        }}
        hint="Only admins see this. It is never sent to the applicant and is not part of any export."
      />
      <div className="flex justify-end">
        <Button
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate(
              { internalNotes: notes },
              {
                onSuccess: () => {
                  toast.success('Note saved.');
                },
                onError: (cause) => {
                  toast.error(
                    cause instanceof ApiError ? cause.message : 'That note could not be saved.',
                  );
                },
              },
            );
          }}
        >
          {save.isPending ? 'Saving…' : 'Save note'}
        </Button>
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">{title}</h2>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** Something the form allowed them to leave blank. Says so, rather than a dash. */
function Absent({ children }: { children: React.ReactNode }) {
  return <span className="italic text-ink-500">{children}</span>;
}

function Chips({ label, values, empty }: { label: string; values: string[]; empty: string }) {
  return (
    <div>
      <p className="text-meta text-ink-500">{label}</p>
      {values.length === 0 ? (
        <p className="mt-1 text-body italic text-ink-500">{empty}</p>
      ) : (
        <ul className="mt-1.5 flex flex-wrap gap-1.5">
          {values.map((value) => (
            <li
              key={value}
              className="rounded-full bg-ink-100 px-2.5 py-0.5 text-meta text-ink-700"
            >
              {value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
